import {
  isMonthYearComplete,
  validateOptionalMonthYear,
} from "./datePartsValidation";

export type AddressStep5Slice = {
  street: string;
  streetType: string;
  streetNumber: string;
  floorPiso: string;
  floorDoor: string;
  municipality: string;
  locality: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
  residenceSince: { month?: string; year?: string };
};

function normalizeInnerSpaces(s: string): string {
  return s.trim().replace(/\s+/g, " ");
}

/** 1-100 chars: letters, marks, digits, spaces, periods, commas. */
export function isValidAddressStreet(raw: string): boolean {
  const s = normalizeInnerSpaces(raw);
  if (s.length < 1 || s.length > 100) return false;
  return /^[\p{L}\p{M}0-9\s.,]+$/u.test(s);
}

/** 1-10: letters, marks, digits, `/` (e.g. S/N). */
export function isValidAddressStreetNumber(raw: string): boolean {
  const s = raw.trim();
  if (s.length < 1 || s.length > 10) return false;
  return /^[\p{L}\p{M}0-9/]+$/u.test(s);
}

/** Optional 0-10: letters, digits, spaces (matches backend; e.g. puerta "12 A" or combined hints). */
export function isValidAddressFloorDoor(raw: string): boolean {
  const s = normalizeInnerSpaces(raw);
  if (!s) return true;
  if (s.length > 10) return false;
  return /^[\p{L}\p{M}0-9\s]+$/u.test(s);
}

/** 1-40: letters, marks, spaces, hyphens, apostrophes. */
export function isValidAddressCity(raw: string): boolean {
  const s = normalizeInnerSpaces(raw);
  if (s.length < 1 || s.length > 40) return false;
  return /^[\p{L}\p{M}\s'’-]+$/u.test(s);
}

export function isValidSpainPostalFormat(raw: string): boolean {
  const s = raw.trim();
  return s.length === 5 && /^\d{5}$/.test(s);
}

function monthYearHasAnyPart(m: { month?: string; year?: string }): boolean {
  return !!(String(m.month ?? "").trim() || String(m.year ?? "").trim());
}

const currentCalendarYear = () => new Date().getFullYear();

/** First invalid scroll target id for Step 5, or null when the step can continue. */
export function getAddressStep5FirstInvalidFieldId(data: AddressStep5Slice): string | null {
  const street = data.street.trim();
  if (!street || !isValidAddressStreet(data.street)) return "field-street";
  if (!data.streetType.trim()) return "field-streetType";

  const sn = data.streetNumber.trim();
  if (!sn || !isValidAddressStreetNumber(data.streetNumber)) return "field-streetNumber";

  if (data.floorDoor.trim() && !isValidAddressFloorDoor(data.floorDoor)) return "field-floorDoor";

  const city = data.locality.trim() || data.city.trim();
  if (!city || !isValidAddressCity(city)) return "field-locality";
  if (!data.municipality.trim()) return "field-municipality";

  if (!data.province.trim()) return "field-province";

  if (!isValidSpainPostalFormat(data.postalCode)) return "field-postalCode";

  if (!data.country.trim()) return "field-country";

  const rs = data.residenceSince;
  if (monthYearHasAnyPart(rs) && !isMonthYearComplete(rs.month ?? "", rs.year ?? "")) {
    return "field-residenceSince";
  }
  const cy = currentCalendarYear();
  if (
    validateOptionalMonthYear(rs.month ?? "", rs.year ?? "", {
      disallowFuture: true,
      minYear: 1950,
      maxYear: cy,
    }) !== null
  ) {
    return "field-residenceSince";
  }

  return null;
}
